/**
 * Helpers for the in-app Halo support form embed. Pure functions kept out of the
 * Svelte components so the load / retry / origin / sizing logic is unit-testable.
 */

// Same-origin host page for Halo's embed (the portal itself can't be iframed cross-origin: X-Frame-Options SAMEORIGIN), whose CSP meta keeps Halo's third-party assets out of the app's policy.
const HALO_FORM_PAGE = '/halo-form.html';

/**
 * Genuine iframe load failures (`onerror`) to retry — reloading with a
 * cache-busting `?retry=n` — before showing the error state. Mirrors RetroLens's
 * `maxRetries`. Only real load failures count: a slow-but-working embed is never
 * treated as an error (see SupportFormModal's reveal-on-load behaviour).
 */
export const MAX_SUPPORT_RETRIES = 3;

/**
 * After the iframe fires `onload`, how long to wait for its `halo-form-loaded`
 * handshake (ms) before treating the frame as a failed page load — a browser
 * offline/error page fires `onload` too. A real load posts the handshake almost
 * immediately.
 */
export const SUPPORT_ONLOAD_GRACE_MS = 5_000;

/**
 * If the form is still masked after this long (ms), show a non-blocking hint
 * offering the support portal — without tearing the embed down. Fires before
 * SUPPORT_LOAD_TIMEOUT_MS so the escape link appears while the load may still
 * complete.
 */
export const SUPPORT_SLOW_HINT_MS = 5_000;

/**
 * Hard ceiling on a single load attempt (ms). If the form hasn't signalled ready
 * by now, stop waiting and show the error state (portal fallback + "Try again")
 * rather than spinning forever on a stalled or very slow connection.
 */
export const SUPPORT_LOAD_TIMEOUT_MS = 20_000;

/**
 * Halo embed config passed to the iframe page as query params. Sourced from
 * `PUBLIC_HALO_LINK` (see `haloEmbedConfig` in `#lib/config.js`) so the ticket type
 * is env-driven and has a single source of truth — no hardcoding in two places,
 * and dev/test environments can route to a non-prod Halo queue.
 */
interface HaloEmbedConfig {
	apiUrl: string;
	ticketTypeId: number;
	ticketTypeKey: string;
}

// Written into the hidden SafeSystem freetext fields via the `env`/`app`/`hostUrl` query params; a value is set verbatim (empty clears Halo's default), an omitted key leaves the field alone.
interface HaloFormPrefill {
	environment?: string;
	application?: string;
	hostUrl?: string;
}

/**
 * Build the iframe `src`. The Halo config (when provided) is threaded in as query
 * params the iframe page reads on load. A non-zero retry appends a cache-busting
 * `retry` param so the browser re-fetches rather than serving the failed load.
 */
export function buildHaloFormSrc(
	retry: number,
	config?: HaloEmbedConfig | null,
	prefill?: HaloFormPrefill | null
): string {
	const params = new URLSearchParams();
	if (config) {
		params.set('apiUrl', config.apiUrl);
		params.set('ticketTypeId', String(config.ticketTypeId));
		params.set('key', config.ticketTypeKey);
	}
	if (prefill?.environment != null) params.set('env', prefill.environment);
	if (prefill?.application != null) params.set('app', prefill.application);
	if (prefill?.hostUrl != null) params.set('hostUrl', prefill.hostUrl);
	if (retry > 0) params.set('retry', String(retry));
	const query = params.toString();
	return query ? `${HALO_FORM_PAGE}?${query}` : HALO_FORM_PAGE;
}

// Halo custom-field ids for the hidden SafeSystem group; the portal fallback reads these directly as `customfield_NNN=` params (the embed page static/halo-form.js hardcodes the same ids).
const HALO_FIELD_ENV = 'customfield_281';
const HALO_FIELD_APP = 'customfield_282';
const HALO_FIELD_HOST_URL = 'customfield_283';

/**
 * Append the prefill values to the Halo portal fallback URL as `customfield_NNN=`
 * params, which the portal (unlike the embed) pre-populates from the query string.
 * Returns the base URL unchanged if it can't be parsed or there's nothing to add.
 */
export function buildHaloFallbackHref(baseUrl: string, prefill?: HaloFormPrefill | null): string {
	if (!prefill) return baseUrl;
	try {
		const url = new URL(baseUrl);
		if (prefill.environment != null) url.searchParams.set(HALO_FIELD_ENV, prefill.environment);
		if (prefill.application != null) url.searchParams.set(HALO_FIELD_APP, prefill.application);
		if (prefill.hostUrl != null) url.searchParams.set(HALO_FIELD_HOST_URL, prefill.hostUrl);
		return url.toString();
	} catch {
		return baseUrl;
	}
}

type HaloMessage =
	| { type: 'ready' }
	| { type: 'loaded' }
	| { type: 'resize'; height: number }
	| { type: 'error'; resource: string };

/** Parse a same-origin message from the Halo iframe page (static/halo-form.js); anything else is null. */
export function parseHaloMessage(event: MessageEvent, expectedOrigin: string): HaloMessage | null {
	if (event.origin !== expectedOrigin) return null;
	const data = event.data as { type?: string; height?: unknown; resource?: unknown } | null;
	switch (data?.type) {
		case 'halo-form-ready':
			return { type: 'ready' };
		case 'halo-form-loaded':
			return { type: 'loaded' };
		case 'halo-form-resize':
			return typeof data.height === 'number' && data.height > 0
				? { type: 'resize', height: data.height }
				: null;
		case 'halo-form-error':
			return {
				type: 'error',
				resource: typeof data.resource === 'string' ? data.resource : 'unknown'
			};
		default:
			return null;
	}
}
